import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, User } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlog();
  }, [slug]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/blogs/${slug}`);
      setBlog(response.data);
    } catch (error) {
      console.error('Error fetching blog:', error);
      toast.error('Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" data-testid="blog-loading">
        <div className="w-12 h-12 border-4 border-brand-pearl/20 border-t-brand-pearl rounded-full animate-spin" />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" data-testid="blog-not-found">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold text-brand-pearl mb-4">Article Not Found</h2>
          <Link to="/blogs" className="text-brand-walnut hover:text-brand-pearl">Back to Blogs</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="blog-detail-page">
      <article className="section-spacing">
        <div className="container-custom max-w-4xl">
          <Link
            to="/blogs"
            data-testid="back-to-blogs-btn"
            className="inline-flex items-center text-brand-walnut hover:text-brand-pearl mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blogs
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-6">
              <div className="flex items-center space-x-4 text-sm text-brand-walnut mb-4">
                <span className="flex items-center">
                  <User className="w-4 h-4 mr-2" />
                  {blog.author}
                </span>
                <span className="flex items-center">
                  <Clock className="w-4 h-4 mr-2" />
                  {formatDate(blog.created_at)}
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-heading font-bold text-brand-pearl mb-6" data-testid="blog-title">
                {blog.title}
              </h1>
            </div>

            <div className="mb-8 rounded-sm overflow-hidden border border-brand-pearl/10">
              <img src={blog.image_url} alt={blog.title} className="w-full h-auto" data-testid="blog-image" />
            </div>

            <div className="prose prose-lg max-w-none" data-testid="blog-content">
              <p className="text-xl text-brand-walnut leading-relaxed mb-6">{blog.excerpt}</p>
              <div className="text-brand-walnut leading-relaxed whitespace-pre-line">{blog.content}</div>
            </div>
          </motion.div>
        </div>
      </article>
    </div>
  );
};

export default BlogDetailPage;